// src/admin/pages/DealroomsAdminPage.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Database, 
  Users, 
  UserCheck,
  Settings,
  FileText
} from 'lucide-react';
import MasterAdminHeader from '../components/MasterAdminHeader';
import MasterAdminSidebar from '../components/MasterAdminSidebar';
import { useGlobalStore } from '../../state/globalStore';
import DealroomsListTab from './tabs/DealroomsListTab';
import PartiesListTab from './tabs/PartiesListTab';
import TeamsListTab from './tabs/TeamsListTab';
import DataroomFileSettingsPage from './DataroomFileSettingsPage';
import StorageManagementTab from './tabs/StorageManagementTab';

const DealroomsAdminPage = () => {
  const { user, userProfile } = useGlobalStore();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get active tab from URL or default to dealrooms
  const getActiveTabFromPath = () => {
    const path = location.pathname.replace('/adminhq/dealrooms', '').replace(/^\//, '');
    if (!path || path === '') return 'dealrooms';
    return path;
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromPath());

  // Dealrooms-specific navigation items
  const dealroomsNavItems = [
    { id: 'dealrooms', name: 'Dealrooms', icon: Database, color: 'blue' },
    { id: 'parties', name: 'Parties', icon: Users, color: 'green' },
    { id: 'teams', name: 'Teams', icon: UserCheck, color: 'purple' },
    { id: 'files', name: 'Files', icon: FileText, color: 'orange' },
    { id: 'file-settings', name: 'File Settings', icon: Settings, color: 'gray' },
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/adminhq/dealrooms/${tabId}`);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dealrooms':
        return <DealroomsListTab />;
      case 'parties':
        return <PartiesListTab />;
      case 'teams':
        return <TeamsListTab />;
      case 'files':
        return <StorageManagementTab />;
      case 'file-settings':
        return <DataroomFileSettingsPage />;
      default:
        return <DealroomsListTab />;
    }
  };

  // Redirect to first tab if no tab is specified
  useEffect(() => {
    const path = location.pathname.replace('/adminhq/dealrooms', '').replace(/^\//, '');
    if (!path || path === '') {
      navigate('/adminhq/dealrooms/dealrooms', { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <MasterAdminSidebar activeTab="dealrooms" setActiveTab={(tab) => navigate(`/adminhq/${tab}`)} />
      <div className="flex-1 flex flex-col">
        <MasterAdminHeader 
          user={user} 
          userProfile={userProfile} 
          title="Dealrooms Admin"
        />
        
        {/* Tabs Bar */}
        <div className="bg-white border-b border-gray-200 px-6">
          <div className="flex space-x-1">
            {dealroomsNavItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleTabChange(item.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === item.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <item.icon className="h-4 w-4 mr-2" />
                  {item.name}
                </div>
              </button>
            ))}
          </div>
        </div>

        <main className="flex-1 overflow-auto">
          <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DealroomsAdminPage;

